import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import {
  isOnboardingFullyCompleted,
  ONBOARDING_API_STEP_KEYS,
  ONBOARDING_TOTAL_STEPS,
} from '@features/onboarding/completion';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useClientsQuery } from '@features/client/queries';
import { GuideStep } from './onboarding-guide-types';
import { useOnboardingProgressQuery } from '@features/onboarding/queries';
import { useVisitsQuery } from '@features/visits/queries';
import { Paths, ROUTES, buildHairDresserPath } from '@shared/AppRoutes';
import {
  openClientFormDialog,
  openServiceFormDialog,
  openStockItemFormDialog,
  openVisitFormDialog,
} from '@shared/layout/layout-bottom-bar/layout-bottom-bar-actions';
import OnboardingStepCard from './OnboardingStepCard';
import { useAppNavigate } from '@shared/store';
import { useSubscriptionPlan } from '@shared/store';
import useIntl from 'react-intl/src/components/useIntl';
import { StepDetailContent } from './StepDetailContent';
import { useOnboardingGuide } from './onboarding-guide.hooks';
import {
  APP_INSTALL_DONE_STORAGE_KEY,
  ONBOARDING_STEP_KEYS,
} from '@features/onboarding/guide-visibility';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

const OnboardingGuide = () => {
  const intl = useIntl();
  const steps = useOnboardingGuide();
  const navigate = useAppNavigate();
  const subscriptionPlan = useSubscriptionPlan();
  const { data: clients } = useClientsQuery();
  const { data: visits } = useVisitsQuery({});
  const { data } = useOnboardingProgressQuery();
  const { trackEvent, trackOnboardingStep } = useAnalytics();
  const [isAppInstallDone, setIsAppInstallDone] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setIsAppInstallDone(
      isStandalone || window.localStorage.getItem(APP_INSTALL_DONE_STORAGE_KEY) === 'true',
    );

    const markInstalled = () => {
      window.localStorage.setItem(APP_INSTALL_DONE_STORAGE_KEY, 'true');
      setIsAppInstallDone(true);
    };

    window.addEventListener('appinstalled', markInstalled);
    return () => window.removeEventListener('appinstalled', markInstalled);
  }, []);

  const markAppInstallDone = () => {
    window.localStorage.setItem(APP_INSTALL_DONE_STORAGE_KEY, 'true');
    setIsAppInstallDone(true);
  };

  const reservationClient = clients?.find(client => !client.deletedAt) ?? clients?.[0];
  const visitToComplete =
    visits?.find(({ visit }) => !visit.visitStatus) ??
    visits?.find(({ visit }) => visit.visitStatus) ??
    null;

  const isStepDone = (step: (typeof steps)[number]) =>
    step.key === 'appInstallGuide'
      ? isAppInstallDone
      : Boolean(data?.[step.key as keyof NonNullable<typeof data>]);
  const completedSteps = steps.filter(isStepDone).length;
  const isAllStepsDone = completedSteps === steps.length;
  const isFullyCompleted = isOnboardingFullyCompleted(data, isAppInstallDone);

  useEffect(() => {
    ONBOARDING_API_STEP_KEYS.forEach(stepKey => {
      if (data?.[stepKey]) {
        trackOnboardingStep(stepKey, ONBOARDING_STEP_KEYS.indexOf(stepKey) + 1);
      }
    });
    if (isAppInstallDone) trackOnboardingStep('appInstallGuide', 1);
  }, [data, isAppInstallDone, trackOnboardingStep]);

  useEffect(() => {
    if (isFullyCompleted) {
      trackEvent(
        'onboarding_fully_completed',
        { total_steps: ONBOARDING_TOTAL_STEPS },
        { once: true },
      );
    }
  }, [isFullyCompleted, trackEvent]);

  const nextStep = steps.find(step => !isStepDone(step));

  const hasRequiredSubscription = (requiredSubscription?: GuideStep['subscription']): boolean => {
    if (!requiredSubscription) {
      return true;
    }

    if (!subscriptionPlan) {
      return false;
    }

    if (requiredSubscription === 'pro') {
      return subscriptionPlan === 'pro' || subscriptionPlan === 'expert';
    }

    return subscriptionPlan === requiredSubscription;
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
        {intl.formatMessage(
          {
            id: 'dashboard.onboarding.subtitle',
          },
          {
            completed: completedSteps,
            total: steps.length,
            highlight: chunks => (
              <Box
                component="span"
                sx={{
                  color: isAllStepsDone ? '#2FBF6B' : '#E45A5A',
                  fontWeight: 700,
                }}
              >
                {chunks}
              </Box>
            ),
          },
        )}
      </Typography>
      {nextStep && (
        <Typography variant="caption" color="text.secondary" fontSize={11}>
          {intl.formatMessage(
            {
              id: 'dashboard.onboarding.nextStep',
              defaultMessage: 'Další doporučený krok: {step}',
            },
            { step: nextStep.title },
          )}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="stretch"
        sx={{ minWidth: 0, overflowX: 'auto', pb: 1, scrollSnapType: 'x proximity' }}
      >
        {completedSteps > 0 && (
          <ButtonBase
            aria-pressed={showCompleted}
            onClick={() => setShowCompleted(value => !value)}
            sx={{
              flexShrink: 0,
              width: { xs: 72, sm: 96 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: showCompleted ? 'primary.main' : 'divider',
              bgcolor: showCompleted ? '#FCEAF1' : 'background.paper',
              color: 'success.main',
              p: 1,
              '&.Mui-focusVisible': { outline: '2px solid', outlineOffset: 2 },
            }}
          >
            <Stack spacing={1} alignItems="center">
              {showCompleted ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
              <Typography fontWeight={700}>{completedSteps}</Typography>
              <Typography variant="body2" fontWeight={600}>
                {intl.formatMessage({
                  id: showCompleted
                    ? 'dashboard.onboarding.hideCompleted'
                    : 'dashboard.onboarding.showCompleted',
                  defaultMessage: showCompleted ? 'Skrýt splněné' : 'Zobrazit splněné',
                })}
              </Typography>
            </Stack>
          </ButtonBase>
        )}
        {isAllStepsDone && !showCompleted && (
          <Typography sx={{ p: 2 }} color="text.secondary" role="status">
            {intl.formatMessage({
              id: 'dashboard.onboarding.allVisibleStepsDone',
              defaultMessage:
                'Všechny kroky průvodce máte splněné. Můžete si je znovu zobrazit vlevo.',
            })}
          </Typography>
        )}
        {steps.map((step, index) => {
          const isDone = isStepDone(step);
          if (isDone && !showCompleted) return null;
          const isLockedBySubscription = !hasRequiredSubscription(step.subscription);

          const handleStepClick = () => {
            if (isLockedBySubscription) {
              return;
            }

            if (step.key === 'visitCreated') {
              openVisitFormDialog();
              return;
            }

            if (step.key === 'appInstallGuide') {
              return;
            }

            if (step.key === 'visitCompleted') {
              if (visitToComplete) {
                navigate(Paths.visitDetail(visitToComplete.client.id, visitToComplete.visit.id));
                return;
              }

              navigate(buildHairDresserPath(ROUTES.visits.path));
              return;
            }

            if (step.key === 'serviceCreated') {
              openServiceFormDialog();
              return;
            }

            if (step.key === 'stockItemCreated') {
              openStockItemFormDialog();
              return;
            }

            if (step.key === 'clientCreated') {
              openClientFormDialog();
              return;
            }

            if (step.key === 'workScheduleConfigured') {
              navigate(`${buildHairDresserPath(ROUTES.profile.path)}#profile-working-schedule`);
              return;
            }

            if (step.key === 'reservationEnabled') {
              if (reservationClient) {
                navigate(Paths.clientDetail(reservationClient.id));
                return;
              }

              navigate(buildHairDresserPath(ROUTES.clients.path));
              return;
            }

            if (step.key === 'publicReservationSetup') {
              navigate(`${buildHairDresserPath(ROUTES.profile.path)}#public-profile`);
              return;
            }

            navigate(step.href);
          };

          return (
            <Box
              key={step.key}
              sx={{
                minWidth: { xs: 240, sm: 312 },
                maxWidth: { xs: 240, sm: 312 },
                flex: '0 0 auto',
                scrollSnapAlign: 'start',
              }}
            >
              <OnboardingStepCard
                detailContent={
                  <StepDetailContent step={step} onAppInstallDone={markAppInstallDone} />
                }
                index={index + 1}
                title={step.title}
                description={step.description}
                ctaLabel={step.ctaLabel}
                isDone={isDone}
                doneLabel={intl.formatMessage({
                  id: 'dashboard.onboarding.status.done',
                  defaultMessage: 'Splněno',
                })}
                todoLabel={intl.formatMessage({
                  id: 'dashboard.onboarding.status.todo',
                  defaultMessage: 'Další krok',
                })}
                requiredSubscription={step.subscription}
                isSubscriptionLocked={isLockedBySubscription}
                openDetailOnClick={step.key === 'appInstallGuide'}
                onClick={handleStepClick}
              />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default OnboardingGuide;
